/**
 * The wizard's three files — the logo, the uploaded font, the study's sources —
 * and the WORDMARK it draws when there is no logo at all.
 *
 * Every rejection is a sentence, per file, checked before any bytes leave the
 * browser (spec §8). The wordmark is here rather than on the logo step because
 * it FOLLOWS later choices: a new highlight colour or caption face redraws it
 * while the operator watches, which is only visible from the shell that owns
 * both.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type Dispatch } from "react";
import { uploadBrandStudyFile } from "../../../api";
import { classifyAsset } from "../../../lib/brandSetup/assets";
import { drawWordmark } from "../../../lib/brandSetup/wordmark";
import {
  MAX_SOURCES,
  effectiveFontNames,
  type BrandSetupAction,
  type BrandSetupSource,
  type BrandSetupState,
} from "../../../lib/brandSetup/state";

const MAX_LOGO_BYTES = 4 * 1024 * 1024;
const MAX_FONT_BYTES = 6 * 1024 * 1024;
export const LOGO_GONE_SENTENCE = "Your logo isn't saved in this browser — add it again here.";
export const FONT_GONE_SENTENCE = "Your uploaded font didn't survive the refresh — add it again here.";

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error("read failed"));
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.readAsDataURL(file);
  });
}

export function useBrandAssets(args: {
  state: BrandSetupState;
  dispatch: Dispatch<BrandSetupAction>;
  /** True once the walk has reached (or passed) the logo step — the wordmark
   *  is drawn when it is about to be seen, not before. */
  reachedLogo: boolean;
  /** True once the walk has gone PAST the logo step (the furthest step is a
   *  later one), whichever screen is showing now. */
  passedLogo: boolean;
  /**
   * The study saw a logo on the reel (`studyFoundLogo(state)`). Then the
   * "YOUR LOGO" placeholder holds the reel's spot THROUGH the logo step —
   * the screen is asking for the file that replaces it (spec round-2 §2.3) —
   * and the wordmark is drawn only once the operator has continued past that
   * step without an upload (`passedLogo`). Keyed to the furthest step rather
   * than the current one so a refresh on the logo step (welcome → resume)
   * cannot draw the wordmark before the step has been seen.
   */
  studyFoundLogo: boolean;
  needsLogo: boolean;
  needsFont: boolean;
}) {
  const { state, dispatch, reachedLogo, passedLogo, studyFoundLogo } = args;
  const drawWordmarkNow = studyFoundLogo ? passedLogo : reachedLogo;
  const [uploading, setUploading] = useState<{ name: string; pct: number } | null>(null);
  const [sourceError, setSourceError] = useState<string | null>(null);
  const [fontError, setFontError] = useState<string | null>(null);
  const [fontNote, setFontNote] = useState<string | null>(args.needsFont ? FONT_GONE_SENTENCE : null);
  const [logoError, setLogoError] = useState<string | null>(null);
  const [logoNote, setLogoNote] = useState<string | null>(args.needsLogo ? LOGO_GONE_SENTENCE : null);
  const [logoFileName, setLogoFileName] = useState<string | null>(null);
  const drawnRef = useRef("");

  /**
   * Type and size are checked HERE, before any bytes leave the browser, and
   * every rejected file gets its OWN sentence — dropping five files must not
   * report only the last problem.
   */
  const addFiles = useCallback(
    async (files: FileList | File[]): Promise<void> => {
      const all = Array.from(files);
      const problems: string[] = [];
      const room = MAX_SOURCES - state.sources.length;
      const picked = all.slice(0, Math.max(0, room));
      if (picked.length < all.length) {
        problems.push(`We can read ${MAX_SOURCES} things at a time — ${all.length - picked.length} were left out.`);
      }
      const added: BrandSetupSource[] = [];
      for (const file of picked) {
        const verdict = classifyAsset(file);
        if ("error" in verdict) {
          problems.push(verdict.error);
          continue;
        }
        setUploading({ name: file.name, pct: 0 });
        try {
          const up = await uploadBrandStudyFile(file, (pct) => setUploading({ name: file.name, pct }));
          added.push({ kind: "upload", uploadId: up.uploadId, filename: up.filename, assetKind: up.kind });
        } catch (e) {
          problems.push(`${file.name} didn't upload: ${e instanceof Error ? e.message : String(e)}`);
        }
      }
      setUploading(null);
      setSourceError(problems.length ? problems.join("\n") : null);
      if (added.length) dispatch({ type: "patch", patch: { sources: [...state.sources, ...added] } });
    },
    [state.sources],
  );

  const handleLogo = useCallback(async (file: File): Promise<void> => {
    if (!/^image\/(png|jpe?g|webp)$/.test(file.type)) return setLogoError("Your logo needs to be a PNG, JPG or WebP.");
    if (file.size > MAX_LOGO_BYTES) return setLogoError("That logo is over 4 MB — a smaller one renders just as well.");
    setLogoError(null);
    try {
      dispatch({ type: "logo", dataUrl: await fileToDataUrl(file), kind: "upload" });
      setLogoFileName(file.name);
      setLogoNote(null);
    } catch {
      setLogoError("We couldn't read that logo file — try saving it again, or pick another.");
    }
  }, []);

  const handleFontFile = useCallback(async (file: File): Promise<void> => {
    if (!/\.(ttf|otf)$/i.test(file.name)) return setFontError("A font has to be a .ttf or .otf file.");
    if (file.size > MAX_FONT_BYTES) return setFontError("That font file is over 6 MB.");
    setFontError(null);
    const family = file.name.replace(/\.(ttf|otf)$/i, "").replace(/[^A-Za-z0-9_-]/g, "") || "BrandFont";
    try {
      const dataUrl = await fileToDataUrl(file);
      dispatch({ type: "fonts", fonts: { uploaded: { dataUrl, family }, body: family } });
      setFontNote(null);
    } catch {
      setFontError("We couldn't read that font file — try another.");
    }
  }, []);


  const fontNames = useMemo(() => effectiveFontNames(state), [state]);
  const hasUploadedLogo = state.logoKind === "upload" && !!state.logoDataUrl;
  const brandName = state.brandName.trim();
  const accent = state.colors.accent;

  /**
   * The brand's name, drawn as its logo (spec §2.5) — so nobody is stopped by
   * a logo they don't have, and so the wordmark FOLLOWS the later choices: a
   * new highlight colour or caption face redraws it while the operator
   * watches. Never a refusal: `drawWordmark` falls back through the body face
   * to the system sans, and a canvas that cannot be made at all just leaves
   * the step asking for an upload.
   */
  useEffect(() => {
    if (!brandName || hasUploadedLogo || !drawWordmarkNow) return;
    const signature = JSON.stringify([brandName, fontNames.display, fontNames.body, accent]);
    if (drawnRef.current === signature && state.logoKind === "wordmark" && state.logoDataUrl) return;
    let live = true;
    const timer = window.setTimeout(() => {
      drawWordmark({
        text: brandName,
        fontFamily: fontNames.display,
        fallbackFamily: fontNames.body,
        color: accent,
      })
        .then((dataUrl) => {
          if (!live) return;
          drawnRef.current = signature;
          dispatch({
            type: "logo",
            dataUrl,
            kind: "wordmark",
            wordmark: { text: brandName, fontFamily: fontNames.display, color: accent },
          });
        })
        .catch(() => {
          if (live) setLogoNote("We couldn't draw your name — upload a logo here instead.");
        });
    }, 180);
    return () => {
      live = false;
      window.clearTimeout(timer);
    };
  }, [brandName, fontNames.display, fontNames.body, accent, hasUploadedLogo, drawWordmarkNow, state.logoKind, state.logoDataUrl]);

  return {
    uploading,
    sourceError,
    setSourceError,
    fontError,
    fontNote,
    logoError,
    logoNote,
    logoFileName,
    fontNames,
    addFiles,
    handleLogo,
    handleFontFile,
  };
}
